import React, { useMemo, useState } from 'react';
import { Search, SquareX } from 'lucide-react';
import type { PermissionAction, Permission, ResourceCatalogEntry } from '../../../types/role';
import { useLanguage } from '../../../features/language/useLanguage';

interface PermissionGridProps {
  catalog: ResourceCatalogEntry[];
  value: Permission[];
  onChange: (next: Permission[]) => void;
  disabled?: boolean;
}

const ALL_ACTIONS: PermissionAction[] = ['read', 'write', 'delete'];

// Semantic groupings for the resource catalog. Mirrors the conceptual
// structure of the app — keeps the permission grid scannable once the
// catalog grows past a dozen entries.
const CATEGORY_ORDER = [
  'operations',
  'profiles',
  'sales',
  'team',
  'intelligence',
  'company',
  'other',
] as const;

type CategoryKey = (typeof CATEGORY_ORDER)[number];

const RESOURCE_TO_CATEGORY: Record<string, CategoryKey> = {
  purchase_orders: 'operations',
  suppliers: 'operations',
  inventory: 'operations',
  'work_orders.roasting': 'operations',
  'work_orders.packing': 'operations',
  recipes: 'operations',
  green_bean_profiles: 'profiles',
  taste_profiles: 'profiles',
  roast_profiles: 'profiles',
  artisan_profiles: 'profiles',
  cupping_sessions: 'profiles',
  sales_orders: 'sales',
  customers: 'sales',
  members: 'team',
  machines: 'team',
  machine_types: 'team',
  roasters: 'team',
  qora_assistant: 'intelligence',
  'dashboard.avg_shrinkage': 'intelligence',
  'dashboard.outstanding_payment': 'intelligence',
  'dashboard.revenue': 'intelligence',
  'dashboard.expenses': 'intelligence',
  'dashboard.net_profit': 'intelligence',
  'dashboard.green_bean_value': 'intelligence',
  'dashboard.roasted_value': 'intelligence',
  'dashboard.finished_value': 'intelligence',
  'dashboard.species': 'intelligence',
  'dashboard.varietas': 'intelligence',
  'dashboard.recent_activities': 'intelligence',
  'dashboard.expiring_soon': 'intelligence',
  'dashboard.low_stock': 'intelligence',
  'reports.roasting': 'intelligence',
  'reports.green_beans': 'intelligence',
  'reports.roasted_beans': 'intelligence',
  'reports.business': 'intelligence',
  activity_logs: 'intelligence',
  company: 'company',
};

// Converts `dashboard.financials` or `purchase_orders` → "Dashboard Financials".
// Used as the final fallback when neither the i18n label nor a manual override
// exists — keeps the UI readable even if the catalog adds a new key upstream.
const humanize = (raw: string): string =>
  raw
    .split(/[._]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

export const PermissionGrid: React.FC<PermissionGridProps> = ({
  catalog,
  value: rawValue,
  onChange,
  disabled,
}) => {
  const value = useMemo(() => rawValue ?? [], [rawValue]);
  const { t } = useLanguage();
  const [search, setSearch] = useState('');

  const has = (obj: string, act: string) => value.some((p) => p.obj === obj && p.act === act);

  const toggle = (obj: string, act: string) => {
    if (disabled) return;
    if (has(obj, act)) {
      onChange(value.filter((p) => !(p.obj === obj && p.act === act)));
    } else {
      onChange([...value, { obj, act }]);
    }
  };

  const clearRow = (resource: ResourceCatalogEntry) => {
    if (disabled) return;
    onChange(value.filter((p) => p.obj !== resource.key));
  };

  const grantRowAll = (resource: ResourceCatalogEntry) => {
    if (disabled) return;
    const withoutRow = value.filter((p) => p.obj !== resource.key);
    const additions = resource.actions.map((act) => ({ obj: resource.key, act }));
    onChange([...withoutRow, ...additions]);
  };

  const grantCategoryAll = (resources: ResourceCatalogEntry[]) => {
    if (disabled) return;
    const keys = new Set(resources.map((r) => r.key));
    const withoutCat = value.filter((p) => !keys.has(p.obj));
    const additions = resources.flatMap((r) =>
      r.actions.map((act) => ({ obj: r.key, act })),
    );
    onChange([...withoutCat, ...additions]);
  };

  const clearCategory = (resources: ResourceCatalogEntry[]) => {
    if (disabled) return;
    const keys = new Set(resources.map((r) => r.key));
    onChange(value.filter((p) => !keys.has(p.obj)));
  };

  const resourceLabel = (r: ResourceCatalogEntry) =>
    t(r.label_key, undefined, humanize(r.key));
  const resourceDescription = (r: ResourceCatalogEntry) => {
    // Derive description key from label key: roles.resources.X → roles.resourceDescriptions.X
    const descKey = r.label_key.replace(
      'roles.resources.',
      'roles.resourceDescriptions.',
    );
    return t(descKey, undefined, '');
  };

  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter((r) => {
      const label = resourceLabel(r).toLowerCase();
      const desc = resourceDescription(r).toLowerCase();
      return label.includes(q) || r.key.toLowerCase().includes(q) || desc.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, search]);

  const grouped = useMemo(() => {
    const map = new Map<CategoryKey, ResourceCatalogEntry[]>();
    for (const r of filteredCatalog) {
      const cat = RESOURCE_TO_CATEGORY[r.key] ?? 'other';
      const arr = map.get(cat) ?? [];
      arr.push(r);
      map.set(cat, arr);
    }
    return CATEGORY_ORDER.filter((k) => map.has(k)).map((k) => ({
      key: k,
      resources: map.get(k)!,
    }));
  }, [filteredCatalog]);

  const totalPossible = useMemo(
    () => catalog.reduce((sum, r) => sum + r.actions.length, 0),
    [catalog],
  );
  const selectedCount = useMemo(() => {
    const valid = new Set<string>();
    for (const r of catalog) {
      for (const act of r.actions) valid.add(`${r.key}::${act}`);
    }
    return value.filter((p) => valid.has(`${p.obj}::${p.act}`)).length;
  }, [catalog, value]);

  const summary = disabled
    ? t('roles.editor.readOnlySummary', { total: totalPossible }, `${totalPossible} permissions`)
    : t(
        'roles.editor.selectedSummary',
        { selected: selectedCount, total: totalPossible },
        `${selectedCount} of ${totalPossible} permissions enabled`,
      );

  return (
    <div className="space-y-4" data-test-id="permission-grid">
      {/* Header — search + summary */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            {t('roles.editor.permissionsTitle', undefined, 'Permissions')}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {t(
              'roles.editor.permissionsSubtitle',
              undefined,
              'Toggle which actions this role can perform on each resource.',
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('roles.editor.searchPlaceholder', undefined, 'Search resources…')}
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full sm:w-56"
              data-test-id="permission-grid-search"
            />
          </div>
          <span className="text-xs font-medium text-gray-500 whitespace-nowrap tabular-nums">
            {summary}
          </span>
        </div>
      </div>

      {/* Groups */}
      {grouped.length === 0 && (
        <div className="text-center py-10 border border-dashed border-gray-200 rounded-md text-sm text-gray-500">
          {t('roles.editor.noMatches', undefined, 'No resources match your search.')}
        </div>
      )}

      <div className="space-y-4">
        {grouped.map((group) => {
          const catResources = group.resources;
          const catSelected = value.filter((p) =>
            catResources.some((r) => r.key === p.obj && r.actions.includes(p.act as PermissionAction)),
          ).length;
          const catTotal = catResources.reduce((sum, r) => sum + r.actions.length, 0);
          return (
            <section
              key={group.key}
              className="border border-gray-200 rounded-lg overflow-hidden bg-white"
            >
              <header className="flex items-center justify-between gap-4 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                <div className="flex items-baseline gap-2 min-w-0">
                  <h4 className="text-sm font-semibold text-gray-800 truncate">
                    {t(`roles.categories.${group.key}`, undefined, humanize(group.key))}
                  </h4>
                  <span className="text-xs text-gray-500 tabular-nums whitespace-nowrap">
                    {catSelected}/{catTotal}
                  </span>
                </div>
                {!disabled && (
                  <div className="flex items-center gap-1 text-xs shrink-0">
                    <button
                      type="button"
                      onClick={() => grantCategoryAll(catResources)}
                      className="px-2 py-1 text-white hover:bg-primary-700 rounded bg-primary-600 transition-all duration-200 active:scale-95"
                    >
                      {t('roles.grid.grantAll', undefined, 'Grant all')}
                    </button>
                    <span className="text-gray-300">·</span>
                    <button
                      type="button"
                      onClick={() => clearCategory(catResources)}
                      className="p-1 text-red-500 hover:bg-red-100 rounded transition-all duration-200 active:scale-95"
                    >
                      <SquareX size={22} />
                    </button>
                  </div>
                )}
              </header>

              <ul className="divide-y divide-gray-100">
                {catResources.map((resource) => {
                  const label = resourceLabel(resource);
                  const description = resourceDescription(resource);
                  return (
                    <li
                      key={resource.key}
                      className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 hover:bg-gray-50/60 group"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {label}
                          </span>
                        </div>
                        {description && (
                          <p className="text-xs text-gray-500 mt-0.5 leading-snug">{description}</p>
                        )}
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                        {ALL_ACTIONS.map((act) => {
                          const supported = resource.actions.includes(act);
                          const active = supported && has(resource.key, act);
                          const actionLabel = t(
                            `roles.grid.action.${act}`,
                            undefined,
                            act.charAt(0).toUpperCase() + act.slice(1),
                          );
                          if (!supported) {
                            return (
                              <span
                                key={act}
                                aria-hidden="true"
                                className="inline-flex h-7 min-w-[68px] px-2.5 shrink-0"
                              />
                            );
                          }
                          return (
                            <button
                              key={act}
                              type="button"
                              disabled={disabled}
                              onClick={() => toggle(resource.key, act)}
                              aria-pressed={active}
                              data-test-id={`perm-${resource.key}-${act}`}
                              className={[
                                'inline-flex items-center justify-center h-7 min-w-[68px] px-2.5 rounded-md text-xs font-medium border',
                                'transition-[background-color,border-color,color,box-shadow,transform] duration-200 ease-out',
                                'active:scale-[0.96]',
                                'disabled:cursor-not-allowed disabled:opacity-60 disabled:active:scale-100',
                                active
                                  ? 'bg-primary-600 border-primary-600 text-white shadow-sm hover:bg-primary-700'
                                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-400',
                              ].join(' ')}
                            >
                              {actionLabel}
                            </button>
                          );
                        })}
                      </div>

                      {!disabled && (
                        <div className="flex items-center gap-1 text-[11px] shrink-0 sm:ml-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={() => grantRowAll(resource)}
                            className="px-1.5 py-0.5 text-white hover:bg-primary-700 rounded bg-primary-600 transition-all duration-200 active:scale-95"
                          >
                            {t('roles.grid.grantAll', undefined, 'Grant all')}
                          </button>
                          <span className="text-gray-300">·</span>
                          <button
                            type="button"
                            onClick={() => clearRow(resource)}
                            className="px-1.5 py-0.5 text-red-500 hover:bg-red-100 rounded transition-all duration-200 active:scale-95"
                          >
                            <SquareX size={20} />
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
};
